
import { FSharpSet__Contains, ofSeq } from "../fable_modules/fable-library-js.5.3.0/Set.js";
import { comparePrimitives } from "../fable_modules/fable-library-js.5.3.0/Util.js";
import { Union, Record } from "../fable_modules/fable-library-js.5.3.0/Types.js";
import { union_type, option_type, string_type, record_type, int32_type } from "../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { fromInt32, op_Multiply, compare, op_Division, toInt64_unchecked } from "../fable_modules/fable-library-js.5.3.0/BigInt.js";
import { FSharpResult$2 } from "../fable_modules/fable-library-js.5.3.0/Result.js";
import { substring, join, concat, isNullOrWhiteSpace } from "../fable_modules/fable-library-js.5.3.0/String.js";

export const acceptedContentTypes = ofSeq(["image/jpeg", "image/png", "image/webp"], {
    Compare: (x, y) => (comparePrimitives(x, y) | 0),
});

export class ImageDimensions extends Record {
    constructor(Width, Height) {
        super();
        this.Width = (Width | 0);
        this.Height = (Height | 0);
    }
}

export function ImageDimensions_$reflection() {
    return record_type("Shared.Photo.ImageDimensions", [], ImageDimensions, () => [["Width", int32_type], ["Height", int32_type]]);
}

/**
 * A photo the upload endpoint accepted. Dimensions are measured client-side, and only when a caller asks for a small-image check.
 */
export class UploadedPhoto extends Record {
    constructor(PhotoKey, PhotoUrl, ThumbnailUrl, Dimensions) {
        super();
        this.PhotoKey = PhotoKey;
        this.PhotoUrl = PhotoUrl;
        this.ThumbnailUrl = ThumbnailUrl;
        this.Dimensions = Dimensions;
    }
}

export function UploadedPhoto_$reflection() {
    return record_type("Shared.Photo.UploadedPhoto", [], UploadedPhoto, () => [["PhotoKey", string_type], ["PhotoUrl", string_type], ["ThumbnailUrl", string_type], ["Dimensions", option_type(ImageDimensions_$reflection())]]);
}

/**
 * Why an upload failed, as the user needs to know it. Raw server or exception text never reaches the UI.
 */
export class UploadFailure extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["TooLarge", "WrongType", "NetworkFailure", "ServerFailure"];
    }
}

export function UploadFailure_$reflection() {
    return union_type("Shared.Photo.UploadFailure", [], UploadFailure, () => [[], [], [], []]);
}

export function UploadFailureModule_message(_arg) {
    switch (_arg.tag) {
        case 0:
            return `The photo must be ${toInt64_unchecked(op_Division(15728640n, 1048576n))} MB or smaller`;
        case 2:
            return "The photo could not be uploaded. Check your connection and try again";
        case 3:
            return "Something went wrong uploading the photo. Try again";
        default:
            return "The photo must be a JPG, PNG or WebP";
    }
}

/**
 * 413 -> TooLarge; 400/415 -> WrongType (the server rejects unreadable or mismatched images with 400);
 * everything else -> ServerFailure.
 */
export function UploadFailureModule_ofHttpStatus(status) {
    switch (status) {
        case 400:
        case 415:
            return new UploadFailure(1, []);
        case 413:
            return new UploadFailure(0, []);
        default:
            return new UploadFailure(3, []);
    }
}

export function PhotoFile_validateSize(sizeBytes) {
    if (compare(sizeBytes, 15728640n) <= 0) {
        return new FSharpResult$2(0, [undefined]);
    }
    else {
        return new FSharpResult$2(1, ["File size exceeds maximum of 15 MB"]);
    }
}

export function PhotoFile_validateContentType(contentType) {
    if (isNullOrWhiteSpace(contentType)) {
        return new FSharpResult$2(1, ["File type is required. Accepted types: JPEG, PNG, WebP"]);
    }
    else if (FSharpSet__Contains(acceptedContentTypes, contentType.toLowerCase())) {
        return new FSharpResult$2(0, [undefined]);
    }
    else {
        return new FSharpResult$2(1, [concat("File type \'", contentType, "\' is not accepted. Accepted types: JPEG, PNG, WebP")]);
    }
}

export function PhotoFile_validate(sizeBytes, contentType) {
    const matchValue = PhotoFile_validateSize(sizeBytes);
    if (matchValue.tag === 0) {
        const matchValue_1 = PhotoFile_validateContentType(contentType);
        if (matchValue_1.tag === 0) {
            return new FSharpResult$2(0, [undefined]);
        }
        else {
            return new FSharpResult$2(1, [matchValue_1.fields[0]]);
        }
    }
    else {
        return new FSharpResult$2(1, [matchValue.fields[0]]);
    }
}

/**
 * Client-side pre-upload check. Delegates to `validate`'s rules, in the same order, and classifies the failure.
 */
export function PhotoFile_validateForUpload(sizeBytes, contentType) {
    let input_6;
    const input_1 = PhotoFile_validateSize(sizeBytes);
    input_6 = ((input_1.tag === 1) ? (new FSharpResult$2(1, [new UploadFailure(0, [])])) : (new FSharpResult$2(0, [input_1.fields[0]])));
    if (input_6.tag === 1) {
        return new FSharpResult$2(1, [input_6.fields[0]]);
    }
    else {
        let input_4;
        const input_3 = PhotoFile_validateContentType(contentType);
        input_4 = ((input_3.tag === 1) ? (new FSharpResult$2(1, [new UploadFailure(1, [])])) : (new FSharpResult$2(0, [input_3.fields[0]])));
        if (input_4.tag === 1) {
            return new FSharpResult$2(1, [input_4.fields[0]]);
        }
        else {
            return new FSharpResult$2(0, [undefined]);
        }
    }
}

/**
 * Pixel-dimension rule guarding against decompression bombs. Dimensions are only
 * known server-side after identifying the image, so this is not part of `validate`.
 */
export function PhotoFile_validateDimensions(width, height) {
    if ((width <= 0) ? true : (height <= 0)) {
        return new FSharpResult$2(1, ["Image has no pixels"]);
    }
    else if ((width > 10000) ? true : (height > 10000)) {
        return new FSharpResult$2(1, ["Image dimensions exceed maximum of 10000 px per side"]);
    }
    else if (compare(toInt64_unchecked(op_Multiply(toInt64_unchecked(fromInt32(width)), toInt64_unchecked(fromInt32(height)))), 40000000n) > 0) {
        return new FSharpResult$2(1, ["Image exceeds maximum of 40 megapixels"]);
    }
    else {
        return new FSharpResult$2(0, [undefined]);
    }
}

/**
 * True when either side of `actual` is below `minimum`.
 */
export function isSmallerThan(minimum, actual) {
    if (actual.Width < minimum.Width) {
        return true;
    }
    else {
        return actual.Height < minimum.Height;
    }
}

export const acceptAttribute = join(",", acceptedContentTypes);

export const formatsHint = `JPG, PNG or WebP, up to ${toInt64_unchecked(op_Division(15728640n, 1048576n))} MB.`;

function stripExtension(key) {
    const lastDot = key.lastIndexOf(".") | 0;
    if (lastDot > 0) {
        return substring(key, 0, lastDot);
    }
    else {
        return key;
    }
}

/**
 * Derive a thumbnail key from an original photo key.
 * Thumbnails are always stored as JPEG, so the key always ends with "_thumb.jpg"
 * regardless of the original format.
 * Example: "photos/abc.png" -> "photos/abc_thumb.jpg"
 */
export function getThumbnailKey(key) {
    return stripExtension(key) + "_thumb.jpg";
}

/**
 * Construct the serving URL path for a photo key.
 * E.g., "photos/abc.jpg" -> "/api/photos/photos/abc.jpg"
 */
export function getPhotoUrl(key) {
    return concat("/api/photos/", key);
}

/**
 * Construct the serving URL path for a thumbnail.
 */
export function getThumbnailUrl(key) {
    return getPhotoUrl(getThumbnailKey(key));
}

/**
 * Derive the 1200x630 cover rendition key from an original photo key.
 * Renditions are always JPEG, so the key always ends with "_cover.jpg".
 * Example: "photos/abc.png" -> "photos/abc_cover.jpg"
 */
export function getCoverKey(key) {
    return stripExtension(key) + "_cover.jpg";
}

/**
 * Construct the serving URL path for a cover rendition.
 */
export function getCoverUrl(key) {
    return getPhotoUrl(getCoverKey(key));
}

//# sourceMappingURL=Photo.js.map
